import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput'; // Changed from CKEditorComponent
import FileInput from '../../components/FileInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import testimonialApi from '../../api/testimonialApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditTestimonial = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    client_designation: '',
    content: '',
    image: undefined, // Use undefined initially to indicate no file selected/changed
    is_active: 0,
  });
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [clearImageFlag, setClearImageFlag] = useState(false); // New state for explicit image removal
  const [testimonialFound, setTestimonialFound] = useState(true);

  const getAvatarUrl = (name) => {
    const encodedName = encodeURIComponent(name);
    return `https://ui-avatars.com/api/?background=384b72&color=fff&name=${encodedName}&size=128&bold=true`;
  };

  useEffect(() => {
    const fetchTestimonial = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await testimonialApi.getTestimonialByIdentifier(id);
        if (response.success && response.data) {
          const testimonialToEdit = response.data;
          setFormData({
            name: testimonialToEdit.name,
            client_designation: testimonialToEdit.client_designation,
            content: testimonialToEdit.content,
            image: undefined, // Ensure image is undefined on fetch, not null
            is_active: testimonialToEdit.is_active ? 1 : 0,
          });
          setCurrentImageUrl(testimonialToEdit.image);
          setClearImageFlag(false); // Reset flag on fetch
          setTestimonialFound(true);
        } else {
          setTestimonialFound(false);
          setGeneralError(response.message || 'Testimonial not found.');
        }
      } catch (err) {
        console.error('Failed to fetch testimonial:', err);
        setTestimonialFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load testimonial. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonial();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (name === 'image' && files && files[0]) {
      setClearImageFlag(false); // If a new image is selected, clear the removal flag
    }
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Removed handleContentChange as TextAreaInput uses standard onChange
  // const handleContentChange = (content) => {
  //   setFormData({ ...formData, content: content });
  //   if (validationErrors.content) {
  //     setValidationErrors(prev => ({ ...prev, content: undefined }));
  //   }
  // };

  const handleImageRemove = () => {
    setFormData({ ...formData, image: null }); // Set to null to indicate explicit removal
    setCurrentImageUrl(null);
    setClearImageFlag(true); // Set the flag to indicate image should be cleared on backend
    if (validationErrors.image) {
      setValidationErrors(prev => ({ ...prev, image: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'image') {
        // Only append 'image' if it's a File object (a new image has been selected)
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        }
        // If formData[key] is undefined, it means no change to the image field,
        // so we don't append it to FormData at all.
        // If formData[key] is null, we handle it with clearImageFlag, so don't append 'image' field either.
      } else {
        data.append(key, formData[key]);
      }
    }

    // Append the clearImageFlag if it's true
    if (clearImageFlag) {
      data.append('clear_image', 'true');
    }

    data.append('_method', 'PUT');

    try {
      await testimonialApi.updateTestimonial(id, data);
      addToast('Testimonial updated successfully!', 'success');
      navigate(ROUTES.TESTIMONIALS);
    } catch (err) {
      console.error('Failed to update testimonial:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update testimonial. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!testimonialFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Testimonial Not Found</h1>
        <p className="text-text-gray">{generalError || 'The testimonial you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.TESTIMONIALS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Testimonials List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Testimonial</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Client Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Client Name"
          error={validationErrors?.name?.[0]}
        />
        <TextInput
          label="Client Designation"
          name="client_designation"
          id="client_designation"
          value={formData.client_designation}
          onChange={handleChange}
          required={true}
          placeholder="Client Designation"
          error={validationErrors?.client_designation?.[0]}
        />
        <TextAreaInput // Changed from CKEditorComponent
          label="Content"
          name="content" // Added name prop
          id="content" // Added id prop
          value={formData.content}
          onChange={handleChange} // Use standard handleChange
          required={true}
          rows={4}
          placeholder="Content"
          error={validationErrors?.content?.[0]}
        />
        <FileInput
          label="Client Image"
          name="image"
          id="image"
          onChange={handleChange}
          error={validationErrors?.image?.[0]}
          currentFileMessage={
            <div className="flex items-center space-x-2">
              <span>Current Image:</span>
              {currentImageUrl ? (
                <a href={currentImageUrl} target="_blank" rel="noopener noreferrer" className="text-text-white hover:underline">View</a>
              ) : (
                <img src={getAvatarUrl(formData.name)} alt="Placeholder" className="h-10 w-10 rounded-full object-cover" />
              )}
              <Button
                type="button"
                onClick={handleImageRemove}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                Remove
              </Button>
            </div>
          }
        />
        <CheckboxInput
          label="Active"
          name="is_active"
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Testimonial
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.TESTIMONIALS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditTestimonial;